import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { createActivity } from '@/lib/crm/api';
import { ACTIVITY_TYPE_OPTIONS } from '@/lib/crm/format';
import { ApiError } from '@/lib/api';
import type { CrmActivity, CrmActivityType, CrmContact } from '@/lib/crm/types';

interface ActivityFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  contacts: CrmContact[];
  onCreated: (activity: CrmActivity) => void;
}

/**
 * Диалог создания активности (AC-1.7..1.10).
 * type обязателен; хотя бы одна из дат planned/completed (валидация клиент+сервер).
 */
export function ActivityForm({
  open,
  onOpenChange,
  companyId,
  contacts,
  onCreated,
}: ActivityFormProps) {
  const [type, setType] = useState<CrmActivityType>('visit');
  const [plannedAt, setPlannedAt] = useState('');
  const [completedAt, setCompletedAt] = useState('');
  const [contactId, setContactId] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setType('visit');
    setPlannedAt('');
    setCompletedAt('');
    setContactId('');
    setNotes('');
    setError(null);
  };

  const toIso = (local: string): string | null =>
    local ? new Date(local).toISOString() : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!plannedAt && !completedAt) {
      setError('Укажите дату плана или дату выполнения (хотя бы одну).');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const created = await createActivity({
        company_id: companyId,
        type,
        planned_at: toIso(plannedAt),
        completed_at: toIso(completedAt),
        contact_id: contactId || null,
        notes: notes.trim() || null,
      });
      onCreated(created);
      reset();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : 'Не удалось создать активность');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent>
        <form onSubmit={handleSubmit} data-testid="activity-form">
          <DialogHeader>
            <DialogTitle>Новая активность</DialogTitle>
            <DialogDescription>
              Зафиксируйте касание клиента. Визит с датой выполнения попадёт в
              CSV-выгрузку для биллинга.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-1">
              <Label htmlFor="activity-type">Тип *</Label>
              <Select
                value={type}
                onValueChange={(v) => setType(v as CrmActivityType)}
              >
                <SelectTrigger id="activity-type" data-testid="activity-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACTIVITY_TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="activity-planned">Запланировано на</Label>
                <Input
                  id="activity-planned"
                  type="datetime-local"
                  value={plannedAt}
                  onChange={(e) => setPlannedAt(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="activity-completed">Выполнено</Label>
                <Input
                  id="activity-completed"
                  type="datetime-local"
                  value={completedAt}
                  onChange={(e) => setCompletedAt(e.target.value)}
                />
              </div>
            </div>

            {contacts.length > 0 && (
              <div className="space-y-1">
                <Label htmlFor="activity-contact">Контакт</Label>
                <Select
                  value={contactId || 'none'}
                  onValueChange={(v) => setContactId(v === 'none' ? '' : v)}
                >
                  <SelectTrigger id="activity-contact">
                    <SelectValue placeholder="Без контакта" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Без контакта</SelectItem>
                    {contacts.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1">
              <Label htmlFor="activity-notes">Заметка</Label>
              <Input
                id="activity-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="о чём говорили / что делать дальше"
              />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Отмена
            </Button>
            <Button type="submit" disabled={submitting} data-testid="activity-submit">
              {submitting ? 'Сохраняем…' : 'Сохранить'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
